import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getTimedTakeaways,
  getVideoQnA,
  getVideoQuestion,
} from '../../api/lfApiVideo';
import { parseSearchParam } from './parseSearchParam';

const QUIZ_ENABLED = process.env.REACT_APP_LF_ENABLE_QUIZ === 'true';

const INITIAL_DETAIL = {
  vid: null,
  takeawaysDone: false,
  qna: [],
  qnaDone: false,
  question: null,
};

export function useVideoQnaState({
  videoId,
  channelId,
  user,
  disableInteractiveFeatures,
  setError,
}) {
  const [videoDetail, setVideoDetail] = useState(INITIAL_DETAIL);
  const [selectedComponentIndex, setSelectedComponentIndex] = useState(-1);
  const [isAskingQuestion, setIsAskingQuestion] = useState(false);
  const [eoc, setEoc] = useState(true);
  const [timedTakeaways, setTimedTakeaways] = useState([]);

  const hasTakeaways = useMemo(
    () => timedTakeaways.length > 0,
    [timedTakeaways],
  );

  const loadVideoDetail = useCallback(
    async (activeVideoId) => {
      if (disableInteractiveFeatures) return;

      const { studyPlanId, lessonId, topicId } = parseSearchParam();

      try {
        const timedRes = await getTimedTakeaways(
          activeVideoId, channelId, studyPlanId, lessonId, topicId, setError);
        if (timedRes?.data?.takeaways?.length) {
          const sorted = [...timedRes.data.takeaways].sort(
            (a, b) => a.start_seconds - b.start_seconds);
          setTimedTakeaways(sorted);
        }
      } catch (error) {
        console.error('Error loading timed takeaways: ', error);
      }

      setVideoDetail((prev) => ({ ...prev, takeawaysDone: true }));

      const questionRes = await getVideoQuestion(activeVideoId, setError);
      if (questionRes?.data) {
        setVideoDetail((prev) => ({ ...prev, question: questionRes.data }));
      }

      if (QUIZ_ENABLED || user?.type === 'premium') {
        const res = await getVideoQnA(activeVideoId, studyPlanId, setError);
        if (res !== null) {
          setVideoDetail((prev) => ({ ...prev, qna: res.data.qna }));
        }
      }

      setVideoDetail((prev) => ({ ...prev, qnaDone: true }));
    },
    [channelId, disableInteractiveFeatures, setError, user?.type],
  );

  // Reset all state and load (or stub) detail whenever videoId changes.
  useEffect(() => {
    if (!videoId) return;

    setVideoDetail({
      ...INITIAL_DETAIL,
      vid: videoId,
      takeawaysDone: disableInteractiveFeatures,
      qnaDone: disableInteractiveFeatures,
    });
    setSelectedComponentIndex(-1);
    setIsAskingQuestion(false);
    setEoc(true);
    setTimedTakeaways([]);

    if (!disableInteractiveFeatures) {
      loadVideoDetail(videoId);
    }
  }, [disableInteractiveFeatures, loadVideoDetail, videoId]);

  // Loading / quiz / takeaways state machine — derived from raw state.
  const currentState = useCallback(() => {
    if (!videoDetail || videoDetail.vid !== videoId
        || !videoDetail.takeawaysDone) {
      return 'LOADING';
    }
    if (!videoDetail.qnaDone) return 'LOADING_QNA';
    if (selectedComponentIndex === -1 && hasTakeaways) {
      return 'TAKEAWAYS_HIDDEN';
    }
    if (videoDetail.qna.length === 0) {
      return hasTakeaways ? 'LAST_QNA' : 'NO_QNA';
    }
    if (selectedComponentIndex < 1) return 'FIRST_QNA';
    if (selectedComponentIndex < videoDetail.qna.length) return 'QNA';
    return 'LAST_QNA';
  }, [hasTakeaways, selectedComponentIndex, videoDetail, videoId]);

  return {
    videoDetail,
    setVideoDetail,
    selectedComponentIndex,
    setSelectedComponentIndex,
    isAskingQuestion,
    setIsAskingQuestion,
    eoc,
    setEoc,
    timedTakeaways,
    hasTakeaways,
    currentState,
  };
}
